import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    Spinner,
    Button,
    Table,
    Nav,
    NavItem,
    NavLink,
} from "reactstrap";
import moment from "moment";
import api from "../services/api";
import Header from "../components/public/Header";
import Footer from "../components/public/Footer";

const LeagueSchedule = () => {
    const { leagueId } = useParams();
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [organization, setOrganization] = useState(null);

    useEffect(() => {
        api.get("/get-org-details/")
            .then((response) => {
                setOrganization(response.data);
            });
    }, []);

    useEffect(() => {
        document.title = "League Schedule - Reserve2Day";

        const fetchData = async () => {
            try {
                const eventResponse = await api.get(`/league-events/?league=${leagueId}`);
                setEvents(eventResponse.data);
            } catch (err) {
                console.error("Error loading league schedule:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [leagueId]);

    if (loading) {
        return (
            <>
                <Header organization={organization} showBanner={false} />
                <Container className="text-center mt-5">
                    <Spinner color="primary" />
                    <p className="mt-3">Loading league schedule...</p>
                </Container>
                <Footer />
            </>
        );
    }

    const scheduleEvents = Array.isArray(events) ? events : [];
    const leagueTitle = scheduleEvents[0]?.league?.title || "League";
    const showLeaderboard = scheduleEvents[0]?.league?.show_leaderboard === true;

    // --- Group events by week ---
    const groupedByWeek = scheduleEvents.reduce((acc, event) => {
        const week = event.league_date?.week || "Unknown";
        if (!acc[week]) acc[week] = [];
        acc[week].push(event);
        return acc;
    }, {});

    // Sort weeks numerically if possible
    const sortedWeeks = Object.keys(groupedByWeek).sort(
        (a, b) => parseInt(a) - parseInt(b)
    );

    return (
        <>
            <Header organization={organization} showBanner={false} />
            <Container className="mt-4">
                <h2 className="text-start mb-4">{leagueTitle}</h2>
                <Nav tabs className="mb-4 league-route-tabs">
                    <NavItem>
                        <NavLink className="active" style={{ cursor: "pointer" }}>
                            Full Schedule
                        </NavLink>
                    </NavItem>
                    {showLeaderboard && (
                        <NavItem>
                            <NavLink
                                style={{ cursor: "pointer" }}
                                onClick={() => navigate(`/league/${leagueId}/scores`)}
                            >
                                Leaderboard
                            </NavLink>
                        </NavItem>
                    )}
                    <NavItem>
                        <NavLink
                            style={{ cursor: "pointer" }}
                            onClick={() => navigate(`/league/${leagueId}/upcoming`)}
                        >
                            Upcoming
                        </NavLink>
                    </NavItem>
                </Nav>

                {scheduleEvents.length === 0 ? (
                    <p className="text-center text-muted">No scheduled events yet.</p>
                ) : (
                    <Row className="g-3">
                        {sortedWeeks.map((week) => {
                            const weekEvents = groupedByWeek[week];

                            // --- Group events by date (e.g., "Oct 25") ---
                            const groupedByDate = weekEvents.reduce((acc, e) => {
                                const dateLabel = moment(e.event?.start).format("ddd MMM DD");
                                if (!acc[dateLabel]) acc[dateLabel] = [];
                                acc[dateLabel].push(e);
                                return acc;
                            }, {});

                            const sortedDates = Object.keys(groupedByDate).sort(
                                (a, b) => moment(a, "MMM DD") - moment(b, "MMM DD")
                            );

                            return (
                                <Col xs={12} md={6} lg={4} key={week}>
                                    <Card className="shadow-sm h-100">
                                        <CardHeader className="fw-semibold text-center">
                                            Week {week}
                                        </CardHeader>
                                        <span className="text-center mt-2">
                                            {weekEvents[0].league_date?.notes || ""}
                                        </span>
                                        <CardBody>
                                            <Table bordered responsive hover className="mb-0 align-middle">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>Team</th>
                                                        <th>Start</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {sortedDates.map((date) => (
                                                        <React.Fragment key={date}>
                                                            <tr className="table-secondary">
                                                                <td colSpan={2} className="fw-semibold text-center">
                                                                    {date}
                                                                </td>
                                                            </tr>
                                                            {groupedByDate[date].map((item) => (
                                                                <tr key={item.id}>
                                                                    <td>{item.team?.name || "Unnamed Team"}</td>
                                                                    <td>{moment(item.event?.start).format("h:mm A")}</td>
                                                                </tr>
                                                            ))}
                                                        </React.Fragment>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </CardBody>
                                    </Card>
                                </Col>
                            );
                        })}
                    </Row>
                )}

                <div className="text-center mt-4 mb-5">
                    <Button outline color="secondary" onClick={() => navigate("/")}>
                        Back to Home
                    </Button>
                </div>
            </Container>
            <Footer />
        </>
    );
};

export default LeagueSchedule;
